/**
 * Offline Transaction Manager
 * Handles saving transactions offline and syncing when back online
 */

class OfflineTransactionManager {
  constructor() {
    this.dbName = 'SalonLogicSolo';
    this.dbVersion = 1;
    this.storeName = 'pending';
    this.db = null;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('[OfflineDB] Error opening database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[OfflineDB] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store for pending transactions
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });
          
          // Create indexes
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('type', 'type', { unique: false });
          objectStore.createIndex('synced', 'synced', { unique: false });
          
          console.log('[OfflineDB] Object store created');
        }
      };
    });
  }

  /**
   * Save transaction to IndexedDB (for offline use)
   */
  async saveTransaction(type, data, token) {
    if (!this.db) {
      await this.init();
    }

    const transaction = {
      type, // 'sale', 'expense', 'mileage'
      data,
      token,
      timestamp: Date.now(),
      synced: false
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.add(transaction);

      request.onsuccess = () => {
        console.log('[OfflineDB] Transaction saved:', request.result);
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('[OfflineDB] Error saving transaction');
        reject(request.error);
      };
    });
  }

  /**
   * Get all pending transactions
   */
  async getPendingTransactions() {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('synced');
      const request = index.getAll(false); // Get all unsynced

      request.onsuccess = () => {
        console.log('[OfflineDB] Retrieved pending transactions:', request.result.length);
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('[OfflineDB] Error getting transactions');
        reject(request.error);
      };
    });
  }

  /**
   * Mark transaction as synced
   */
  async markAsSynced(id) {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readwrite');
      const store = tx.objectStore(this.storeName);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const transaction = getRequest.result;
        if (transaction) {
          transaction.synced = true;
          transaction.syncedAt = Date.now();
          
          const updateRequest = store.put(transaction);
          
          updateRequest.onsuccess = () => {
            console.log('[OfflineDB] Transaction marked as synced:', id);
            resolve();
          };
          
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(); // Transaction not found, maybe already deleted
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete a transaction
   */
  async deleteTransaction(id) {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log('[OfflineDB] Transaction deleted:', id);
        resolve();
      };

      request.onerror = () => {
        console.error('[OfflineDB] Error deleting transaction');
        reject(request.error);
      };
    });
  }

  /**
   * Sync all pending transactions
   */
  async syncAll(api) {
    const pending = await this.getPendingTransactions();
    console.log('[OfflineSync] Syncing', pending.length, 'transactions');

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (const transaction of pending) {
      try {
        // Determine API endpoint based on type
        let endpoint;
        switch (transaction.type) {
          case 'sale':
            endpoint = '/solo/sales';
            break;
          case 'expense':
            endpoint = '/solo/expenses';
            break;
          case 'mileage':
            endpoint = '/solo/mileage';
            break;
          default:
            throw new Error('Unknown transaction type: ' + transaction.type);
        }

        // Sync with API
        const response = await api.post(endpoint, transaction.data);

        if (response.status === 200 || response.status === 201) {
          await this.deleteTransaction(transaction.id);
          results.success++;
          console.log('[OfflineSync] Synced:', transaction.id);
        } else {
          results.failed++;
          results.errors.push(`Transaction ${transaction.id} failed: ${response.statusText}`);
        }
      } catch (error) {
        console.error('[OfflineSync] Error syncing transaction:', error);
        results.failed++;
        results.errors.push(`Transaction ${transaction.id} error: ${error.message}`);
      }
    }

    console.log('[OfflineSync] Results:', results);
    return results;
  }

  /**
   * Get count of pending transactions
   */
  async getPendingCount() {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('synced');
      const request = index.count(false);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Clear all transactions (use with caution!)
   */
  async clearAll() {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([this.storeName], 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[OfflineDB] All transactions cleared');
        resolve();
      };

      request.onerror = () => {
        console.error('[OfflineDB] Error clearing transactions');
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
const offlineManager = new OfflineTransactionManager();
export default offlineManager;
